import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useUser } from '@fs/zion-user'
import { useLazyRequest } from '../hooks'
import {
  getUsersSubscriptions,
  postARCRegistration,
  postDiscoveryDayRegistration,
  postEasyRegistration,
  postExpoHallRegistration,
  postRegistration,
  postSubscription,
  updateUserPreferences,
} from '../api/rootstech/registration'

const initialState = {
  sms: false,
  onlineEvent: false,
  inPersonEvent: false,
  onlineMarketing: false,
  inPersonMarketing: false,
}

const mapSubscriptions = (data) => {
  if (!data) {
    return initialState
  }
  const subscriptionGroups = data?.brazeUserSubscriptionResponse?.users?.[0]?.subscription_groups || []

  // Create a lookup map for O(n) instead of O(n²) complexity
  const subscriptionMap = subscriptionGroups.reduce((acc, group) => {
    acc[group.name] = group.status === 'Subscribed'
    return acc
  }, {})

  return {
    sms: subscriptionMap['RootsTech for SMS/MMS'] || false,
    onlineEvent: subscriptionMap['Online RootsTech Event'] || false,
    onlineMarketing: subscriptionMap['Online RootsTech Marketing'] || false,
    inPersonEvent: subscriptionMap['In-Person RootsTech Event'] || false,
    inPersonMarketing: subscriptionMap['In-Person RootsTech Marketing'] || false,
    // NOTE: The following subscriptions are not returned yet by the API
    // arcEvent: subscriptionMap['RootsTech ARC Event'] || false,
    // expoHallEvent: subscriptionMap['RootsTech Expo Hall Event'] || false,
    // discoveryDayEvent: subscriptionMap['RootsTech Discovery Day Event'] || false,
  }
}

const RegistrationContext = createContext()
export const useRegistrationContext = () => useContext(RegistrationContext)

export function RegistrationProvider({ children }) {
  const { signedIn } = useUser()
  const [refresh, setRefresh] = useState(true)
  const [subscriptions, setSubscriptions] = useState(initialState)

  // Wrap each registration method with useLazyRequest
  const [sendConferenceRegistration, conferenceState] = useLazyRequest(postRegistration)
  const [sendExpoHallRegistration, expoHallState] = useLazyRequest(postExpoHallRegistration)
  const [sendARCRegistration, arcState] = useLazyRequest(postARCRegistration)
  const [sendDiscoveryDayRegistration, discoveryDayState] = useLazyRequest(postDiscoveryDayRegistration)
  const [sendEasyRegistration, easyRegistrationState] = useLazyRequest(postEasyRegistration)
  const [sendSubscription, subscriptionState] = useLazyRequest(postSubscription)
  const [sendChangeSubscriptions, changeSubscriptionsState] = useLazyRequest(updateUserPreferences)

  const handleRefresh = useCallback(
    (response) => {
      if (signedIn && response && !response.error) {
        setRefresh(true)
      }
      return response
    },
    [signedIn]
  )

  useEffect(() => {
    async function doLoad() {
      if (signedIn && refresh) {
        const response = await getUsersSubscriptions()
        setSubscriptions(mapSubscriptions(response.data))
        if (!response.error) {
          setRefresh(false)
        }
      }
    }
    doLoad()
  }, [signedIn, refresh])

  const conferenceRegistration = useCallback(
    async (body) => handleRefresh(await sendConferenceRegistration(body)),
    [sendConferenceRegistration, handleRefresh]
  )

  const expoHallRegistration = useCallback(
    async (body) => handleRefresh(await sendExpoHallRegistration(body)),
    [sendExpoHallRegistration, handleRefresh]
  )

  const arcRegistration = useCallback(
    async (body) => handleRefresh(await sendARCRegistration(body)),
    [sendARCRegistration, handleRefresh]
  )

  const discoveryDayRegistration = useCallback(
    async (body) => handleRefresh(await sendDiscoveryDayRegistration(body)),
    [sendDiscoveryDayRegistration, handleRefresh]
  )

  const subscribe = useCallback(
    async (body) => handleRefresh(await sendSubscription(body)),
    [sendSubscription, handleRefresh]
  )

  const changeSubscriptions = useCallback(
    async (body) => handleRefresh(await sendChangeSubscriptions(body)),
    [sendChangeSubscriptions, handleRefresh]
  )

  const easyRegistration = useCallback(
    async (body) => handleRefresh(await sendEasyRegistration(body)),
    [sendEasyRegistration, handleRefresh]
  )

  const value = useMemo(
    () => ({
      subscriptions,
      subscribe,
      conferenceRegistration,
      expoHallRegistration,
      easyRegistration,
      arcRegistration,
      changeSubscriptions,
      discoveryDayRegistration,
      handleRefresh,
      // Expose states for each registration method
      conferenceState,
      expoHallState,
      arcState,
      discoveryDayState,
      easyRegistrationState,
      subscriptionState,
      changeSubscriptionsState,
    }),
    [
      subscriptions,
      subscribe,
      conferenceRegistration,
      expoHallRegistration,
      easyRegistration,
      arcRegistration,
      changeSubscriptions,
      discoveryDayRegistration,
      handleRefresh,
      conferenceState,
      expoHallState,
      arcState,
      discoveryDayState,
      easyRegistrationState,
      subscriptionState,
      changeSubscriptionsState,
    ]
  )

  return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>
}
